import {
    Box,
    Flex,
    Popover,
    PopoverAnchor,
    PopoverBody,
    PopoverContent,
    Portal,
    Spinner,
    Stack,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Asset } from '../_openapi/portfolio-optimizer';
import { useAssetLookup } from '../api/asset';
import { DebouncedInput } from '../widgets/DebouncedInput';

export const SecuritySelector = ({
    onSelect,
    identifier = '',
    isInvalid = false,
}: {
    onSelect: (asset: Asset) => void;
    identifier?: string;
    isInvalid?: boolean;
}) => {
    const [search, setSearch] = useState(identifier);
    const { data, isLoading, isPending } = useAssetLookup(search);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        setSearch(identifier);
    }, [identifier]);

    const initialized = useRef(false);
    useEffect(() => {
        if (initialized.current) {
            return;
        }

        if (!isLoading && !isOpen && data) {
            const match = data.find(
                ({ sId, cusip, symbol }) => sId === identifier || cusip === identifier || symbol === identifier,
            );
            onSelect(match || ({} as Asset));
            initialized.current = true;
        }
    }, [data, identifier, isLoading, isOpen, onSelect]);

    const searchResults = useMemo(() => {
        const match = search.toLocaleLowerCase();
        return data?.filter(
            ({ sId, cusip, symbol }) =>
                sId.toLowerCase().startsWith(match) ||
                cusip?.toLowerCase().startsWith(match) ||
                symbol?.toLowerCase().startsWith(match),
        );
    }, [data, search]);

    const inputRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClickOutside = (ev: any) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(ev.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(ev.target)
            ) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    return (
        <Popover autoFocus={false} isOpen={isOpen} onClose={onClose} isLazy>
            <PopoverAnchor>
                <Box ref={inputRef}>
                    <DebouncedInput
                        isInvalid={isInvalid}
                        initialValue={identifier}
                        debounceCallback={setSearch}
                        autoComplete="off"
                        onFocus={onOpen}
                        size="sm"
                    />
                </Box>
            </PopoverAnchor>
            <Portal>
                <PopoverContent width="fit-content" fontSize="sm" boxShadow="dark-lg">
                    <PopoverBody>
                        <Stack gap={0} ref={dropdownRef}>
                            {search.length ? (
                                isPending ? (
                                    <Spinner size="sm" />
                                ) : searchResults?.length ? (
                                    <Box>
                                        <Flex px={2} py={1} fontSize="xs" borderBottomWidth={1}>
                                            <Text as="span" width="160px">
                                                Symbol
                                            </Text>
                                            <Text as="span" width="80px" mx={4}>
                                                Cusip
                                            </Text>
                                            <Text as="span" width="80px">
                                                Security ID
                                            </Text>
                                            <Text
                                                width="500px"
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                            >
                                                Description
                                            </Text>
                                        </Flex>
                                        <Box maxHeight="340px" overflowY="auto">
                                            {searchResults.map((asset) => (
                                                <Flex
                                                    key={asset.sId}
                                                    px={2}
                                                    py={1}
                                                    _hover={{ background: 'gray.600' }}
                                                    cursor="pointer"
                                                    borderRadius="sm"
                                                    onClick={() => {
                                                        onSelect(asset);
                                                        onClose();
                                                    }}
                                                    fontSize="xs"
                                                >
                                                    <Text as="span" width="160px">
                                                        {asset.symbol}
                                                    </Text>
                                                    <Text as="span" width="80px" mx={4}>
                                                        {asset.cusip}
                                                    </Text>
                                                    <Text as="span" width="80px">
                                                        {asset.sId}
                                                    </Text>
                                                    <Text
                                                        width="500px"
                                                        whiteSpace="nowrap"
                                                        overflow="hidden"
                                                        textOverflow="ellipsis"
                                                    >
                                                        {asset.description}
                                                    </Text>
                                                </Flex>
                                            ))}
                                        </Box>
                                    </Box>
                                ) : (
                                    <Text>
                                        <i>No match found</i>
                                    </Text>
                                )
                            ) : (
                                <Text>
                                    <i>Start typing to search by ID, Cusip or Symbol</i>
                                </Text>
                            )}
                        </Stack>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
};
